import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Rating, Button, CircularProgress } from "@mui/material";
import { IoBagCheckOutline } from "react-icons/io5";
import { FaHome } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { Helmet } from "react-helmet-async";
import emptyCartImage from "../../assets/images/emptyCart.png";
import { MyContext } from "../../App";
import { deleteData, editData, fetchDataFromApi } from "../../utils/api";
import { useDispatch, useSelector } from "react-redux";
import {
  setCartItems,
  updateCartItem,
  removeFromCart,
} from "../../store/slices/CartSlice";

const Cart = () => {
  const [isLogin, setIsLogin] = useState(false);
  const context = useContext(MyContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    cartItems: cartData,
    total,
    loading: cartLoading,
  } = useSelector((state) => state.cart);

  useEffect(() => {
    window.scrollTo(0, 0);
    context.setEnableFilterTab(false);
    checkLoginStatus();
  }, []);

  const checkLoginStatus = () => {
    const token = localStorage.getItem("token");
    if (token) {
      setIsLogin(true);
    } else {
      navigate("/");
    }
  };

  const updateQuantity = async (item, newQuantity) => {
    const currentPrice = item?.productId?.prices?.find(
      (price) => price._id === item.priceId
    );

    if (!currentPrice) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Selected price option is no longer available. Please remove this item and add it again.",
      });
      return;
    }

    if (newQuantity > currentPrice.countInStock) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Quantity exceeds stock or invalid price.",
      });
      return;
    }

    try {
      const response = await editData(`/api/cart/${item._id}`, {
        quantity: newQuantity,
        priceId: item.priceId,
      });

      if (response?.success) {
        dispatch(
          updateCartItem({
            _id: item._id,
            quantity: newQuantity,
            subTotal: newQuantity * currentPrice.actualPrice,
          })
        );
      }
    } catch (err) {
      console.error("Update quantity failed:", err);
    }
  };

  const handlePriceChange = async (itemId, newPriceId) => {
    try {
      // Validate that the new price exists
      const cartItem = cartData.find((item) => item._id === itemId);
      const newPrice = cartItem?.productId?.prices?.find(
        (price) => price._id === newPriceId
      );

      if (!newPrice) {
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Selected price option is not available.",
        });
        return;
      }

      const response = await editData(`/api/cart/${itemId}`, {
        priceId: newPriceId,
      });

      if (response?.success) {
        // Refresh cart data to get updated information
        const user = JSON.parse(localStorage.getItem("user"));
        if (user?._id) {
          const updatedCart = await fetchDataFromApi(
            `/api/cart?userId=${user._id}`
          );
          dispatch(setCartItems(updatedCart.data));
        }
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Price option updated successfully.",
        });
      }
    } catch (err) {
      console.error("Price change failed:", err);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to update price option.",
      });
    }
  };

  const removeItem = async (id) => {
    try {
      await deleteData(`/api/cart/${id}`);
      dispatch(removeFromCart(id));
      context.setAlertBox({
        open: true,
        error: false,
        msg: "Item removed from cart.",
      });
    } catch (err) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to remove item.",
      });
    }
  };

  if (!isLogin) return null;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Your Cart - SabbaFarm</title>
        <meta name="description" content="Review your cart on SabbaFarm." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://sabbafarm.com/cart" />
        <meta property="og:title" content="Your Cart - SabbaFarm" />
        <meta
          property="og:description"
          content="Review your SabbaFarm cart before checkout."
        />
        <meta property="og:url" content="https://sabbafarm.com/cart" />
        <meta property="og:type" content="website" />
      </Helmet>

      <h1 className="text-3xl font-bold mb-4">Your Cart</h1>
      <p className="mb-6">
        There are{" "}
        <span className="font-bold text-red-600">{cartData.length}</span>{" "}
        products in your cart
      </p>

      {cartLoading ? (
        <div className="flex justify-center items-center h-64">
          <CircularProgress />
        </div>
      ) : cartData.length === 0 ? (
        <div className="text-center">
          <img
            src={emptyCartImage}
            alt="Empty Cart"
            className="mx-auto w-40 mb-4"
          />
          <h2 className="text-2xl font-semibold mb-4">Your Cart is Empty</h2>
          <Link to="/">
            <button className="bg-green-500 hover:bg-green-400 text-white font-bold py-2 px-4 rounded">
              <span className="flex items-center gap-2">
                <FaHome /> Continue Shopping
              </span>
            </button>
          </Link>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-2/3">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price Option
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subtotal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Remove
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cartData?.map((item) => {
                      // Find the selected price using priceId
                      const priceData = item?.productId?.prices?.find(
                        (price) => price._id === item?.priceId
                      );

                      // If price not found, show error state
                      if (!priceData) {
                        return (
                          <tr key={item._id} className="bg-red-50">
                            <td colSpan={6} className="px-6 py-4 text-center">
                              <div className="text-red-600">
                                <strong>Price option not found!</strong>
                                <br />
                                The selected price option for "
                                {item?.productId?.name}" is no longer available.
                                <br />
                                <button
                                  className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                                  onClick={() => removeItem(item._id)}
                                >
                                  Remove Item
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <tr key={item._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <img
                                className="h-10 w-10 rounded-full"
                                src={
                                  item?.productId?.images?.[0] || emptyCartImage
                                }
                                alt="product"
                              />
                              <div className="ml-4 text-sm font-medium">
                                {item?.productId?.name || "Unavailable"}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              {item?.productId?.prices?.length > 1 ? (
                                <select
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                  value={item?.priceId}
                                  onChange={(e) =>
                                    handlePriceChange(item._id, e.target.value)
                                  }
                                >
                                  {item?.productId?.prices?.map((price) => (
                                    <option key={price._id} value={price._id}>
                                      {price.quantity} {price.type} - ₹
                                      {price.actualPrice}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <div className="font-medium">
                                  {priceData?.quantity || "-"}{" "}
                                  {priceData?.type || "-"}
                                </div>
                              )}
                              {!priceData && (
                                <div className="text-xs text-red-600 mt-1">
                                  ⚠️ Price option not found
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              <div className="font-medium">
                                ₹{priceData?.actualPrice || "-"}
                              </div>
                              {priceData?.oldPrice &&
                                priceData?.oldPrice >
                                  priceData?.actualPrice && (
                                  <div className="text-xs text-gray-400 line-through">
                                    ₹{priceData?.oldPrice}
                                  </div>
                                )}
                              {priceData?.discount &&
                                priceData?.discount > 0 && (
                                  <div className="text-xs text-green-600">
                                    {priceData?.discount}% OFF
                                  </div>
                                )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <button
                                className="px-2 py-1 border"
                                onClick={() =>
                                  updateQuantity(
                                    item,
                                    Math.max(1, item.quantity - 1)
                                  )
                                }
                              >
                                -
                              </button>
                              <span className="px-4 py-1 border-t border-b">
                                {item.quantity}
                              </span>
                              <button
                                className="px-2 py-1 border"
                                onClick={() =>
                                  updateQuantity(item, item.quantity + 1)
                                }
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            ₹{item.subTotal}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => removeItem(item._id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <MdDelete
                                size={24}
                                className="text-red-600 hover:text-red-900"
                              />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="lg:w-1/3">
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Cart Summary</h2>
              <div className="flex justify-between mb-2">
                <span>Subtotal</span>
                <span className="font-semibold">₹{total}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span>Shipping</span>
                <span className="font-semibold">Free</span>
              </div>
              <div className="flex justify-between mb-4">
                <span>Total</span>
                <span className="font-semibold text-red-600">₹{total}</span>
              </div>
              <Link to="/checkout">
                <button className="w-full bg-green-500 text-white font-bold py-2 px-4 rounded flex items-center justify-center gap-2 hover:bg-green-600">
                  <IoBagCheckOutline className="text-xl" /> Proceed to Checkout
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
